import { useEffect, useState } from "react";
import { HumanSupportBanner } from "@/components/recruiter/HumanSupport";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { BookOpen, Compass, Feather, Gem, HeartHandshake, LifeBuoy, Map, MessageCircleHeart, NotebookPen, RefreshCw, Sparkles, Sprout, Sun, TrendingUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { WellbeingOverview } from "@/components/wb/Overview";
import { AiLoading, CardState, Crisis, Eyebrow, SaveBtn, SubHeader, WbCard, WbTitle, WisdomQuote } from "@/components/wb/WbUi";
import { api, errMsg } from "@/lib/api";
import { saveItem, targetUrl, useWb, wbPost } from "@/lib/wb";

const EXPLORE = [
  ["/wellbeing/wisdom", "The Wisdom Room", "Timeless ideas for hard seasons", Feather],
  ["/wellbeing/journeys", "Wisdom Journeys", "Guided 5-day series", Map],
  ["/wellbeing/reflection", "A Moment of Reflection", "One question, a few quiet minutes", BookOpen],
  ["/wellbeing/recommendations", "Recommendations", "Gentle next steps for you", Compass],
  ["/wellbeing/progress", "Look How Far You've Come", "Your progress, in one place", TrendingUp],
  ["/wellbeing/value", "Remember Your Value", "Strengths from your own story", Gem],
  ["/wellbeing/growth", "Beyond the Job Search", "Small challenges to grow", Sprout],
  ["/wellbeing/boost", "Daily Boost", "Encouragement for today", Sun],
  ["/wellbeing/companion", "My Career Companion", "Talk it through, any time", HeartHandshake],
  ["/wellbeing/journal", "My Journal", "Entries, saved items, history", NotebookPen],
];

const MessageCard = ({ q }) => {
  const [busy, setBusy] = useState(false);
  const refresh = async () => { setBusy(true); const d = await wbPost("/wellbeing/message/refresh"); if (d) q.setData(d); setBusy(false); };
  return (
    <WbCard gold data-testid="wb-message-card">
      <Eyebrow>A message for you</Eyebrow>
      <CardState q={q} what="Your personal message">
        {busy ? <AiLoading /> : <>
          <p className="font-wb text-lg leading-relaxed text-zinc-100 sm:text-xl" data-testid="wb-message">{q.data?.message}</p>
          {q.data?.why && <p className="mt-3 text-xs text-zinc-400" data-testid="wb-message-why">{q.data.why}</p>}
        </>}
        <div className="mt-4 flex gap-1">
          <SaveBtn testid="wb-message-save" onClick={() => saveItem({ kind: "message", title: "A message for you", text: q.data.message, meta: { why: q.data.why } })} />
          <Button size="sm" variant="ghost" onClick={refresh} disabled={busy} data-testid="wb-message-refresh"><RefreshCw className="h-4 w-4" />Another message</Button>
        </div>
      </CardState>
    </WbCard>
  );
};

const TodayWisdom = ({ q }) => (
  <WbCard data-testid="wb-today-wisdom">
    <Eyebrow>Today's wisdom</Eyebrow>
    <CardState q={q} what="Today's wisdom" loading="Finding today's wisdom…">
      <WisdomQuote item={q.data?.item} compact />
      {q.data?.reflection && <p className="mt-3 text-sm italic text-zinc-400">{q.data.reflection}</p>}
      <Link to="/wellbeing/wisdom" className="mt-4 inline-block text-sm text-[#F3C649] hover:underline" data-testid="wb-open-wisdom">Open the Wisdom Room →</Link>
    </CardState>
  </WbCard>
);

const NextStep = ({ q }) => {
  const r = q.data?.items?.[0];
  return (
    <WbCard data-testid="wb-next-step">
      <Eyebrow>Recommended next step</Eyebrow>
      <CardState q={q} what="Your recommendations">
        {r ? <>
          <p className="text-xs text-zinc-500">What we noticed</p><p className="mb-3 text-sm text-zinc-300">{r.noticed}</p>
          <p className="font-wb text-lg text-zinc-100">{r.action}</p>
          <Link to={targetUrl(r.target)}><Button className="mt-4 rounded-full" data-testid="wb-next-step-cta">{r.cta_label || "Let's do it"}</Button></Link>
        </> : <p className="text-sm text-zinc-500">Check in this week and we'll suggest a gentle next step.</p>}
      </CardState>
    </WbCard>
  );
};

const Footer = () => {
  const [reminder, setReminder] = useState(null);
  const prefs = useWb("/wellbeing/prefs");
  useEffect(() => { api.get("/wellbeing/reminder").then((r) => setReminder(!!r.data.enabled)).catch(() => {}); }, []);
  const toggle = async (enabled) => { try { await api.put("/wellbeing/reminder", { enabled }); setReminder(enabled); } catch (e) { toast.error(errMsg(e)); } };
  const u = prefs.data?.usage;
  return (
    <WbCard className="space-y-4" data-testid="wb-footer">
      <div className="flex flex-wrap items-center justify-between gap-3">
        {reminder !== null && <label className="flex items-center gap-3 text-sm text-zinc-300"><Switch checked={reminder} onCheckedChange={toggle} data-testid="reminder-switch" />Sunday check-in reminder</label>}
        <Link to="/wellbeing/companion"><Button variant="outline" className="rounded-full" data-testid="wb-talk-coach"><MessageCircleHeart className="h-4 w-4 text-[#D4AF37]" />Talk it through with your coach</Button></Link>
      </div>
      {u && u.limit != null && <p className="text-xs text-zinc-500" data-testid="wb-usage">{Math.max(0, u.limit - (u.used || 0))} of {u.limit} free AI refreshes & companion chats left this month · daily content is always free</p>}
      <Crisis />
    </WbCard>
  );
};

export default function Wellbeing() {
  const msg = useWb("/wellbeing/message");
  const wis = useWb("/wellbeing/wisdom/today");
  const rec = useWb("/wellbeing/recommendations");
  const onCheckin = () => { msg.reload(); rec.reload(); wis.reload(); };
  return (
    <div className="space-y-6" data-testid="wellbeing-dashboard">
      <SubHeader title={msg.data?.welcome || "Welcome back"} back={null} subtitle="Your journey is about more than finding your next opportunity. It's also about discovering your strengths, building resilience, and growing along the way." />
      <HumanSupportBanner where="wellbeing" className="mb-0" />
      <div className="grid gap-6 md:grid-cols-2">
        <MessageCard q={msg} />
        <TodayWisdom q={wis} />
      </div>
      <div id="overview"><WbTitle className="mb-4">Your well-being overview</WbTitle><WellbeingOverview onSaved={onCheckin} /></div>
      <NextStep q={rec} />
      <div>
        <WbTitle className="mb-4">Explore</WbTitle>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="wb-explore">
          {EXPLORE.map(([to, t, s, Icon], i) => (
            <Link key={to} to={to} style={{ animationDelay: `${i * 40}ms` }} className="fade-up group flex items-center gap-4 rounded-[20px] border border-[#27272A] bg-[#121214] p-4 transition-colors hover:border-[#D4AF37]/50" data-testid={`wb-tile-${to.split("/").pop()}`}>
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-[#33290D] text-[#D4AF37] transition-transform group-hover:scale-105"><Icon className="h-5 w-5" /></span>
              <span className="min-w-0"><span className="block font-wb text-base text-zinc-100">{t}</span><span className="block text-xs text-zinc-500">{s}</span></span>
            </Link>
          ))}
        </div>
      </div>
      <Footer />
      <p className="flex items-center gap-1.5 text-[11px] text-zinc-600"><LifeBuoy className="h-3 w-3" /><Sparkles className="h-3 w-3" />Go at your own pace. There's no wrong way to use this space.</p>
    </div>
  );
}
